import { CharacterCard } from '../types';
import { getCardDisplayDate } from './cardDateUtils';

export interface DayCards {
  date: Date;
  dayNumber: number;
  dayName: string;
  cards: CharacterCard[];
}

export interface MonthGroup {
  name: string;
  value: string;
  cards: DayCards[];
}

// 日本時間（JST）ベースで日付文字列を生成
export const getJSTDateKey = (date: Date): string => {
  // toLocaleStringを使用してJSTで正確な日付を取得
  const jstDateStr = date.toLocaleDateString('ja-JP', {
    timeZone: 'Asia/Tokyo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
  // YYYY/MM/DD形式をYYYY-MM-DD形式に変換
  return jstDateStr.replace(/\//g, '-');
};

// 日本時間（JST）ベースで月キーを生成
export const getJSTMonthKey = (date: Date): string => {
  // toLocaleStringを使用してJSTで正確な年月を取得
  const jstDateStr = date.toLocaleDateString('ja-JP', {
    timeZone: 'Asia/Tokyo',
    year: 'numeric',
    month: '2-digit'
  });
  // YYYY/MM形式をYYYY-MM形式に変換
  return jstDateStr.replace(/\//g, '-');
};

// カレンダー用の月別グループ化
export const groupCardsByMonth = (cardsToGroup: CharacterCard[]): MonthGroup[] => {
  // 重複カードを除去
  const uniqueCards = cardsToGroup.filter((card, index, arr) => 
    arr.findIndex(c => c.id === card.id) === index
  );
  
  const monthsMap = new Map<string, Map<string, CharacterCard[]>>();

  // カードを月と日でグループ化
  uniqueCards.forEach((card) => {
    // 新しいユーティリティ関数を使用してカードの表示日付を取得
    const date = getCardDisplayDate(card);

    const monthKey = getJSTMonthKey(date);
    const dayKey = getJSTDateKey(date);

    if (!monthsMap.has(monthKey)) {
      monthsMap.set(monthKey, new Map());
    }
    
    const monthData = monthsMap.get(monthKey)!;
    if (!monthData.has(dayKey)) {
      monthData.set(dayKey, []);
    }
    monthData.get(dayKey)!.push(card);
  });

  // MonthGroup形式に変換
  return Array.from(monthsMap.entries()).map(([monthKey, daysMap]) => {
    const [year, month] = monthKey.split('-');
    const monthName = new Date(parseInt(year), parseInt(month) - 1).toLocaleDateString('en-US', { month: 'long' });
    
    const dayCards: DayCards[] = Array.from(daysMap.entries())
      .map(([dayKey, dayCardList]) => {
        // dayKeyはYYYY-MM-DD形式なので、年月日を正確に取得
        const [yearStr, monthStr, dayStr] = dayKey.split('-');
        const year = parseInt(yearStr);
        const month = parseInt(monthStr) - 1; // JavaScriptの月は0ベース
        const day = parseInt(dayStr);

        // ローカルタイムゾーンで正確な日付を作成
        const date = new Date(year, month, day);

        return {
          date,
          dayNumber: day, // 直接使用
          dayName: date.toLocaleDateString('en-US', { weekday: 'long' }),
          cards: dayCardList.sort((a, b) => {
            // 新しいユーティリティ関数を使用してソート
            const dateA = getCardDisplayDate(a);
            const dateB = getCardDisplayDate(b);

            return dateB.getTime() - dateA.getTime();
          })
        };
      })
      .sort((a, b) => b.date.getTime() - a.date.getTime());

    return {
      name: monthName,
      value: monthKey,
      cards: dayCards
    };
  }).sort((a, b) => b.value.localeCompare(a.value));
};